import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import Grid from "@mui/material/Grid";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

// Core Components
import MDCButton from "../../core/MDCButton";
import MDCLabel from "../../core/MDCLabel";
import MDCStepper from "../../core/MDCStepper";
import MDCText from "../../core/MDCText";
import QESProviderBanner from "../../shared/QESProviderBanner";

// Theme
import theme from "../../../themes/mdc-theme";

// Constants
import { QES_SIGNING_START } from "../../../constants/PluginConstants";
import {
  QES_CURRENT_STAGE,
  QES_STAGE_STATUS,
  getNPQESStepperValue,
} from "../../../constants/userConstants";

// Assets
import DownloadFileIcon from "../../iconComponents/DownloadFileIcon";

// Inner Components
import QESSignerUserDetails from "./VollamachtTab/QESSignerUserDetails";

const FLOW_STEP = {
  QES_PROVIDER_DETAIL: "QES_PROVIDER_DETAIL",
  QES_PROVIDER_SIGNING_PROGRESS: "QES_PROVIDER_SIGNING_PROGRESS",
  QES_PROVIDER_SIGNING_COMPLETE: "QES_PROVIDER_SIGNING_COMPLETE",
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    boxSizing: "border-box",
    paddingTop: "1.25rem",
    paddingBottom: "2rem",
  },

  // Bordered card around the stepper/documents — matches the vendor banner.
  stepperCard: {
    border: `1px solid ${theme.palette.MDCColors.color21}`,
    borderRadius: "6px",
    background: theme.palette.MDCColors.white,
    padding: "1.5rem",
  },
  // Stepper labels per XD: small, regular-weight gray text under each circle.
  stepperLabel: {
    fontSize: "0.875rem !important",
    fontWeight: "400 !important",
    color: `${theme.palette.MDCColors.textColor} !important`,
    fontFamily: `${theme.palette.typography.fontFamily} !important`,
    marginTop: "8px !important",
    lineHeight: "1.3 !important",
    textAlign: "center",
  },

  // --- Shared content column ------------------------------------------------
  contentArea: {
    maxWidth: "450px",
    margin: "0 auto",
  },
  // Wider layout for provider progress and signed-document views.
  wideContentArea: {
    maxWidth: "900px",
    margin: "0 auto",
  },
  fieldGroup: {
    marginBottom: "1.25rem",
  },
  fullWidthBtn: {
    marginTop: "1.5rem",
    "& button": {
      width: "100% !important",
    },
  },
  emailError: {
    fontSize: "0.75rem",
    color: theme.palette.MDCColors.errorDanger,
    fontFamily: theme.palette.typography.fontFamily,
    marginTop: "2px",
  },
  pendingArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2.5rem 1rem",
    border: `1px solid ${theme.palette.MDCColors.color21}`,
    borderRadius: "6px",
    minHeight: "160px",
  },
  // --- Step 4: Signing error (Fehlermeldung) --------------------------------
  // Sits between the stepper and the document placeholder; the placeholder
  // stays visible so the layout matches the XD error state.
  signingErrorBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    margin: "1.5rem 0",
    padding: "12px 14px",
    border: `2px solid ${theme.palette.MDCColors.errorDanger}`,
    borderRadius: "5px",
    background: theme.palette.MDCColors.white,
  },
  signingErrorContent: {
    flex: 1,
    minWidth: 0,
  },
  signingErrorTitle: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.MDCColors.textColor,
    fontFamily: theme.palette.typography.fontFamily,
    margin: "0 0 4px",
  },
  signingErrorText: {
    fontSize: "0.875rem",
    color: theme.palette.MDCColors.color18,
    fontFamily: theme.palette.typography.fontFamily,
    margin: 0,
  },
  signingErrorButton: {
    flexShrink: 0,
    "& button": {
      whiteSpace: "nowrap",
      marginTop: 0,
    },
  },
  pendingText: {
    fontSize: "0.875rem",
    color: theme.palette.MDCColors.color18,
    textAlign: "center",
    fontFamily: theme.palette.typography.fontFamily,
    marginTop: "0.75rem",
  },

  // Signed-document rows in the provider completion view.
  signedDocsContainer: {
    marginTop: "1.75rem",
  },
  signedDocRow: {
    display: "flex",
    alignItems: "center",
    // gap: "10px",
    padding: "14px 16px",
    border: `1px solid ${theme.palette.MDCColors.color23}`,
    borderRadius: "6px",
    background: theme.palette.MDCColors.white,
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  signedDocName: {
    flex: 1,
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.MDCColors.textColor,
    fontFamily: theme.palette.typography.fontFamily,
  },
  signedBadge: {
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "0.75rem",
    fontWeight: 600,
    background: "#D1FAE5",
    color: "#065F46",
    fontFamily: theme.palette.typography.fontFamily,
    flexShrink: 0,
    marginRight: "1rem",
  },
  signedDownloadBtn: {
    flexShrink: 0,
    "& button": {
      minWidth: "unset !important",
      padding: "4px !important",
    },
  },
}));

// Provider signing screen
const QESSigningStartScreen = ({
  vendor,
  holderFirstName,
  holderLastName,
  onCancel,
  onStartQESProcess,
  signingStatus,
  documents,
  onDownloadDocument,
  disabled,
  halterFormValues,
  isDetailDialog,
}) => {
  const signingStepLabels = QES_SIGNING_START.SIGNING_STEP_LABELS;
  const classes = useStyles();
  // Resume the NP provider flow from its backend status when already started.
  const getInitialProviderStep = () => {
    if (
      signingStatus?.currentStage === QES_CURRENT_STAGE.SIGN &&
      signingStatus?.stageStatus === QES_STAGE_STATUS.COMPLETED
    ) {
      return FLOW_STEP.QES_PROVIDER_SIGNING_COMPLETE;
    }
    if (signingStatus?.currentStage) {
      return FLOW_STEP.QES_PROVIDER_SIGNING_PROGRESS;
    }
    return FLOW_STEP.QES_PROVIDER_DETAIL;
  };
  const initialStep = getInitialProviderStep();

  const [step, setStep] = useState(initialStep);
  const [qesProviderForm, setQesProviderForm] = useState({
    firstName: holderFirstName || "",
    lastName: holderLastName || "",
    emailId: "",
  });
  const [qesProviderEmailError, setQesProviderEmailError] = useState("");
  const [signingError, setSigningError] = useState(null);

  // Keep the view synchronized with the polled status when a signing flow resumes.
  useEffect(() => {
    const isSignCompleted =
      signingStatus?.currentStage === QES_CURRENT_STAGE.SIGN &&
      signingStatus?.stageStatus === QES_STAGE_STATUS.COMPLETED;
    if (
      isSignCompleted &&
      (step === FLOW_STEP.QES_PROVIDER_DETAIL ||
        step === FLOW_STEP.QES_PROVIDER_SIGNING_PROGRESS)
    ) {
      setStep(FLOW_STEP.QES_PROVIDER_SIGNING_COMPLETE);
    } else if (
      !isSignCompleted &&
      signingStatus?.currentStage &&
      step === FLOW_STEP.QES_PROVIDER_DETAIL
    ) {
      setStep(FLOW_STEP.QES_PROVIDER_SIGNING_PROGRESS);
    }
  }, [signingStatus, step]);

  if (!vendor) return null;

  const validateEmail = (emailId) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailId);
  };

  const handleQESProviderEmailChange = (value) => {
    setQesProviderForm((prev) => ({ ...prev, emailId: value }));
    if (value) setQesProviderEmailError("");
  };

  const handleQESProviderStartSigning = () => {
    if (!validateEmail(qesProviderForm.emailId)) {
      setQesProviderEmailError(QES_SIGNING_START.EMAIL_REQUIRED);
      return;
    }
    setQesProviderEmailError("");
    setSigningError(null);

    const payload = {
      providerId: vendor.providerId,
      additionalFields: {
        halterType: "naturliche",
        emailId: qesProviderForm.emailId,
      },
    };

    onStartQESProcess(payload)
      .then((resp) => {
        if (resp?.status === 201) {
          setStep(FLOW_STEP.QES_PROVIDER_SIGNING_PROGRESS);
        } else {
          const emailError =
            resp?.data?.errorFieldMap?.["additionalFields.emailId"];
          if (emailError) {
            setQesProviderEmailError(emailError);
          } else {
            setSigningError(
              resp?.data?.message || QES_SIGNING_START.SIGNING_ERROR_GENERIC
            );
          }
        }
      })
      .catch((error) => {
        const responseData = error?.response?.data || error?.data;
        const emailError =
          responseData?.errorFieldMap?.["additionalFields.emailId"];
        if (emailError) {
          setQESProviderEmailError(emailError);
        } else {
          setSigningError(
            responseData?.message ||
              error?.message ||
              QES_SIGNING_START.SIGNING_ERROR_GENERIC
          );
        }
      });
  };

  // Provider detail form.
  const renderQESProviderDetail = () => (
    <div className={classes.contentArea}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <MDCLabel label={QES_SIGNING_START.FIRST_NAME_LABEL} />
          <MDCText
            name="qesProviderFirstName"
            value={qesProviderForm.firstName}
            readOnly
          />
        </Grid>
        <Grid item xs={6}>
          <MDCLabel label={QES_SIGNING_START.LAST_NAME_LABEL} />
          <MDCText
            name="qesProviderLastName"
            value={qesProviderForm.lastName}
            readOnly
          />
        </Grid>
      </Grid>

      <Grid container marginTop="1.5rem">
        <Grid item xs={12}>
          <div className={classes.fieldGroup}>
            <MDCLabel label={QES_SIGNING_START.EMAIL_LABEL} />
            <MDCText
              name="emailId"
              value={qesProviderForm.emailId}
              onChange={(e) => handleQESProviderEmailChange(e.target.value)}
              placeholder={QES_SIGNING_START.EMAIL_PLACEHOLDER}
              error={Boolean(qesProviderEmailError)}
              disabled={disabled}
            />
            {qesProviderEmailError && (
              <p className={classes.emailError}>{qesProviderEmailError}</p>
            )}
          </div>
        </Grid>
      </Grid>

      <div className={classes.fullWidthBtn}>
        <MDCButton
          variant="contained"
          label="Signatur starten"
          onClick={handleQESProviderStartSigning}
          disabled={disabled || !validateEmail(qesProviderForm.emailId)}
        />
      </div>
      {signingError && <p className={classes.emailError}>{signingError}</p>}
    </div>
  );

  // Signing progress is driven by the polled provider status.
  const renderSigningProgress = () => {
    const backendErrorMessage = signingStatus?.errorMessage;
    const showSigningError = Boolean(backendErrorMessage);

    // NP 4-step stepper driven by the polled signingStatus (notification →
    // ident → sign). See getNPQESStepperValue for the stage→step mapping.
    const qesProviderStatusStep = getNPQESStepperValue(signingStatus);
    return (
      <div className={classes.wideContentArea}>
        <div className={classes.stepperCard}>
          <MDCStepper
            step={qesProviderStatusStep}
            steps={signingStepLabels}
            labelClassName={classes.stepperLabel}
            alternativeLabel
          />

          {showSigningError && (
            <div className={classes.signingErrorBox}>
              <div className={classes.signingErrorContent}>
                <p className={classes.signingErrorTitle}>
                  {QES_SIGNING_START.SIGNING_ERROR_TITLE}
                </p>
                <p className={classes.signingErrorText}>
                  {backendErrorMessage ||
                    QES_SIGNING_START.SIGNING_ERROR_PLACEHOLDER}
                </p>
              </div>
              <div className={classes.signingErrorButton}>
                <MDCButton
                  variant="contained"
                  label={QES_SIGNING_START.RESTART_SIGNING_BUTTON}
                  onClick={handleQESProviderStartSigning}
                  disabled={disabled}
                />
              </div>
            </div>
          )}

          <div className={classes.pendingArea}>
            <DescriptionOutlinedIcon
              style={{
                fontSize: "2.5rem",
                color: theme.palette.MDCColors.color21,
              }}
            />
            <p className={classes.pendingText}>
              {QES_SIGNING_START.SIGNING_PENDING_MESSAGE}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderSigningComplete = () => {
    const signedDocuments = documents || [];
    return (
      <div className={classes.wideContentArea}>
        <div className={classes.stepperCard}>
          <MDCStepper
            step={signingStepLabels.length}
            steps={signingStepLabels}
            labelClassName={classes.stepperLabel}
            alternativeLabel
          />
          <Grid
            container
            spacing={3}
            marginTop={"1rem"}
            className={classes.signedDocsContainer}
          >
            {signedDocuments.map((doc) => (
              <Grid item xs={12} sm={6} key={doc.fileKey || doc.label}>
                <div
                  className={classes.signedDocRow}
                  onClick={() =>
                    onDownloadDocument &&
                    onDownloadDocument(doc.fileKey || doc.label)
                  }
                >
                  <span className={classes.signedDocName}>{doc.label}</span>
                  <span className={classes.signedBadge}>
                    {QES_SIGNING_START.SIGNED_BADGE}
                  </span>
                  <div className={classes.signedDownloadBtn}>
                    <DownloadFileIcon />
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>

          {/* QES Signer User Details */}
          <QESSignerUserDetails
            signingStatus={signingStatus}
            halterFormValues={halterFormValues}
          />
        </div>
      </div>
    );
  };

  const isComplete = step === FLOW_STEP.QES_PROVIDER_SIGNING_COMPLETE;
  const vendorBanner = (
    <QESProviderBanner
      provider={vendor}
      onCancel={onCancel}
      disabled={isComplete || isDetailDialog}
      isComplete={isComplete}
      showCancel={!isComplete}
    />
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const stepContent = {
    [FLOW_STEP.QES_PROVIDER_DETAIL]: renderQESProviderDetail,
    [FLOW_STEP.QES_PROVIDER_SIGNING_PROGRESS]: renderSigningProgress,
    [FLOW_STEP.QES_PROVIDER_SIGNING_COMPLETE]: renderSigningComplete,
  };

  return (
    <div className={classes.root}>
      <div className={classes.wideContentArea}>{vendorBanner}</div>
      {stepContent[step]()}
    </div>
  );
};

QESSigningStartScreen.propTypes = {
  vendor: PropTypes.shape({
    providerId: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
  }).isRequired,
  holderFirstName: PropTypes.string,
  holderLastName: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onDownloadPrefilledDocuments: PropTypes.func,
  signingStatus: PropTypes.object,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      fileKey: PropTypes.string,
    })
  ),
  onDownloadDocument: PropTypes.func,
  onStartQESProcess: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default QESSigningStartScreen;
